import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ArrowRight, ShoppingBag } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Configuration des familles de prestations
const PRESTATIONS_FAMILIES = [
  {
    id: 'jeux', label: 'Jeux & Animations', icon: '🎠',
    desc: 'Châteaux gonflables, parcours, mini golf, escape game…',
    color: 'var(--teal)', bg: 'var(--teal-mist)',
    categories: ['cat-jeux-gonflables', 'cat-jeux-bois', 'cat-animation'],
    univers_filter: 'anniversaire_enfant',
    hero_image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80'
  },
  {
    id: 'photo', label: 'Photo & Photobooth', icon: '📷',
    desc: 'Photobooth premium, 360°, arche, fond photo, livre d\'or audio…',
    color: '#7A5E00', bg: 'rgba(212,175,55,0.08)',
    categories: ['cat-photo'],
    univers_filter: 'mariage',
    hero_image: 'https://images.unsplash.com/photo-1772683530248-e81fcb0faf81?w=800&q=80'
  },
  {
    id: 'food', label: 'Animations gourmandes', icon: '🍬',
    desc: 'Barbe à papa, popcorn, bubble waffle, churros, bar à bonbons…',
    color: '#B94B5C', bg: 'var(--rose-mist)',
    categories: ['cat-food'],
    univers_filter: 'anniversaire_enfant',
    hero_image: 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=800&q=80'
  },
  {
    id: 'brasero', label: 'Brasero & Cuisson live', icon: '🔥',
    desc: 'Brasero XXL, plancha, grillades en live, animation culinaire conviviale…',
    color: '#C58B19', bg: 'rgba(197,139,25,0.08)',
    categories: ['cat-brasero'],
    univers_filter: 'mariage',
    hero_image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&q=80',
    featured: true,  // FORTEMENT MIS EN AVANT
    featured_label: 'Coup de cœur'
  },
  {
    id: 'boissons', label: 'Boissons & Bar', icon: '🍺',
    desc: 'Tireuse bière, bar 3 pressions, fontaine, café, machine glaçons…',
    color: '#0B4B4E', bg: 'var(--teal-mist)',
    categories: ['cat-boissons'],
    univers_filter: 'entreprise',
    hero_image: 'https://images.unsplash.com/photo-1552815534-3193a1faec82?w=800&q=80'
  },
  {
    id: 'deco', label: 'Décoration & Création', icon: '🎨',
    desc: 'Arches ballons, panneaux personnalisés, guirlandes, déco sur-mesure…',
    color: '#B94B5C', bg: 'var(--rose-mist)',
    categories: ['cat-deco'],
    univers_filter: 'mariage',
    hero_image: 'https://images.unsplash.com/photo-1758606137571-79fb4dd7d5a4?w=800&q=80'
  },
  {
    id: 'son', label: 'Son & Lumière', icon: '💡',
    desc: 'Sono, enceintes, vidéoprojecteur, éclairage LED, machine fumée…',
    color: '#4B1A9E', bg: 'rgba(139,92,246,0.08)',
    categories: ['cat-son-lumiere'],
    univers_filter: 'entreprise',
    hero_image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=800&q=80'
  },
  {
    id: 'mobilier', label: 'Mobilier & Structures', icon: '🪑',
    desc: 'Tables, chaises, mange-debout, lounge, barnum 3×6m…',
    color: '#374151', bg: 'rgba(107,114,128,0.08)',
    categories: ['cat-mobilier'],
    univers_filter: 'mariage',
    hero_image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80'
  },
  {
    id: 'fabrication', label: 'Fabrication sur-mesure', icon: '✨',
    desc: 'Vous imaginez, nous fabriquons. Arches, panneaux, déco thémée…',
    color: 'var(--teal)', bg: 'var(--teal-mist)',
    categories: [],
    href: '/fabrication',  // redirect to fabrication page
    hero_image: 'https://images.unsplash.com/photo-1638800042504-4ef3de7b4c2d?w=800&q=80'
  },
];

export default function PrestationsPage() {
  const { famille } = useParams();
  const [activeFamily, setActiveFamily] = useState(famille || null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const { addItem } = useCart();

  const activeFamilyData = PRESTATIONS_FAMILIES.find(f => f.id === activeFamily);

  useEffect(() => {
    if (activeFamily && activeFamilyData?.categories?.length > 0) {
      fetchFamilyProducts();
    }
  // eslint-disable-next-line
  }, [activeFamily]);

  async function fetchFamilyProducts() {
    setLoading(true);
    try {
      if (!activeFamilyData?.categories?.length) return;
      const catId = activeFamilyData.categories[0];
      const res = await fetch(`${BACKEND}/api/products?categorie_id=${catId}&limit=20`);
      const data = await res.json();
      setProducts(data.products || []);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoading(false);
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      {/* Hero */}
      <section className="section-py" style={{ background: 'var(--teal-mist)', borderBottom: '1px solid rgba(93,188,191,0.2)' }}>
        <div className="container-ose text-center">
          <span className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold mb-4" style={{ background: '#FFFFFF', color: '#0B4B4E', border: '1px solid rgba(93,188,191,0.3)' }}>
            Nos prestations
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-4" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
            Toutes nos familles d'animation
          </h1>
          <p className="text-sm max-w-lg mx-auto mb-6" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>
            Jeux, photo, food, brasero, boissons, déco, son, mobilier, fabrication sur-mesure.
            Livraison et installation comprises dans le Lot.
          </p>
          <Link to="/creer-evenement" className="btn-teal px-7 py-3.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2">
            Composer mon événement <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* BRASERO — SECTION SPÉCIALE FORTEMENT MIS EN AVANT */}
      <section className="section-py" style={{ background: 'linear-gradient(135deg, rgba(197,139,25,0.06) 0%, #FFFFFF 100%)' }}>
        <div className="container-ose">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-4"
                style={{ background: 'rgba(197,139,25,0.12)', color: '#7A5200', border: '1px solid rgba(197,139,25,0.25)' }}>
                🔥 Coup de cœur — Brasero & Cuisson live
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mb-4" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                L'animation qui crée l'ambiance
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)', lineHeight: '1.8' }}>
                Le brasero est devenu l'incontournable des garden parties et mariages. 
                Grillades en live, plancha, planches apéritives… une expérience culinaire conviviale 
                qui rassemble tous vos invités autour du feu.
              </p>
              <ul className="space-y-2 mb-6">
                {['Brasero XXL acier noir avec bois et grille', 'Pack brasero + plancha + ustensiles', 'Animation culinaire avec chef en live', 'Formule grazing table'].map((item, i) => (
                  <li key={`brasero-item-${i}`} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                    <span style={{ color: '#C58B19' }}>🔥</span> {item}
                  </li>
                ))}
              </ul>
              <div className="flex gap-3">
                <button onClick={() => setActiveFamily('brasero')} className="btn-teal px-5 py-2.5 rounded-xl text-sm font-semibold">
                  Voir les braseros
                </button>
                <Link to="/fabrication" className="btn-rose px-5 py-2.5 rounded-xl text-sm font-semibold">
                  Sur-mesure
                </Link>
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=600&q=80"
                alt="Brasero garden party"
                className="w-full h-72 object-cover rounded-2xl"
                style={{ boxShadow: '0 20px 60px rgba(197,139,25,0.15)' }} loading="lazy" />
              <div className="absolute -bottom-3 -right-3 px-4 py-2 rounded-xl text-sm font-bold"
                style={{ background: 'rgba(197,139,25,0.12)', border: '1px solid rgba(197,139,25,0.3)', color: '#7A5200', backdropFilter: 'blur(8px)' }}>
                À partir de 100€
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAMILIES GRID */}
      <section className="section-py section-white">
        <div className="container-ose">
          <h2 className="font-display text-2xl font-bold mb-8 text-center" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
            Explorez par famille
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            {PRESTATIONS_FAMILIES.map(f => (
              f.href ? (
                <Link key={f.id} to={f.href}
                  className="card-premium p-5 text-center group cursor-pointer"
                  style={{ borderColor: f.featured ? 'rgba(197,139,25,0.3)' : 'var(--border)', background: f.featured ? 'rgba(197,139,25,0.04)' : '#FFFFFF' }}>
                  <div className="text-3xl mb-2">{f.icon}</div>
                  {f.featured && <span className="text-xs font-bold mb-1 block" style={{ color: '#C58B19' }}>{f.featured_label}</span>}
                  <h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--ink)' }}>{f.label}</h3>
                  <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: '1.4' }}>{f.desc}</p>
                </Link>
              ) : (
                <button key={f.id}
                  onClick={() => setActiveFamily(activeFamily === f.id ? null : f.id)}
                  className="card-premium p-5 text-center group cursor-pointer text-left w-full"
                  style={{ borderColor: activeFamily === f.id ? 'var(--teal)' : f.featured ? 'rgba(197,139,25,0.3)' : 'var(--border)', background: activeFamily === f.id ? 'var(--teal-mist)' : f.featured ? 'rgba(197,139,25,0.04)' : '#FFFFFF', boxShadow: activeFamily === f.id ? '0 0 0 2px rgba(93,188,191,0.2)' : undefined }}>
                  <div className="text-3xl mb-2">{f.icon}</div>
                  {f.featured && <span className="text-xs font-bold mb-1 block" style={{ color: '#C58B19' }}>{f.featured_label}</span>}
                  <h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--ink)' }}>{f.label}</h3>
                  <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: '1.4' }}>{f.desc}</p>
                </button>
              )
            ))}
          </div>

          {/* Products for selected family */}
          {activeFamily && activeFamilyData && (
            <div>
              <h3 className="font-display font-bold text-xl mb-5" style={{ color: 'var(--ink)' }}>
                {activeFamilyData.icon} {activeFamilyData.label}
              </h3>
              {loading ? (
                <div className="flex justify-center py-8"><div className="animate-spin w-8 h-8 rounded-full" style={{ border: '2px solid var(--border)', borderTopColor: 'var(--teal)' }} /></div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {products.map(p => (
                    <div key={p.id} className="card-premium overflow-hidden">
                      <Link to={`/produit/${p.id}`}>
                        <img src={p.images?.[0] || ''} alt={p.nom} className="w-full h-40 object-cover hover:scale-105 transition-transform duration-300" loading="lazy" />
                      </Link>
                      <div className="p-4">
                        <Link to={`/produit/${p.id}`}><h4 className="font-semibold text-sm mb-1 line-clamp-2" style={{ color: 'var(--ink)' }}>{p.nom}</h4></Link>
                        <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>{p.description?.slice(0, 60)}...</p>
                        <div className="flex items-center justify-between">
                          <span className="price-gold text-sm">{p.tarifs?.particulier || 0}€</span>
                          <button onClick={() => { addItem(p); toast.success('Ajouté'); }} className="btn-teal px-3 py-1.5 text-xs font-semibold rounded-lg">Ajouter</button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {products.length === 0 && (
                    <div className="col-span-full text-center py-8">
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Produits bientôt disponibles</p>
                      <Link to="/catalogue" className="btn-teal px-5 py-2 rounded-xl text-sm font-semibold mt-3 inline-flex">Voir tout le catalogue</Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
